<template>
  <div class="gs-app gs-promo-dashboard">
    <v-wait for="promo-dashboard">
      <div v-if="funnelResource" class="gs-promo-dashboard__inner">
        <header class="gs-promo-dashboard__page-header">
          <div class="gs-promo-dashboard__identity">
            <p class="gs-promo-dashboard__eyebrow">{{ whitelabelAppData('sell').title }} · Affiliate</p>
            <h1>{{ portalTitle }}</h1>
            <p class="gs-promo-dashboard__lede">
              Copy your affiliate link, then pick the promo material you want to send.
            </p>
          </div>

          <div class="gs-promo-dashboard__portal gs-refresh-controls">
            <gs-field
              v-if="hasMultipleResources"
              class="gs-promo-dashboard__portal-select"
              :label="$t('frontend_portal')"
            >
              <gs-name-select
                v-model="selectedPromoLinkPermalink"
                size="full"
                :placeholder="$t('frontend_groovesell_promotional_tool')"
                :options="promoToolOptions"
                @change="changedPromoTool"
              />
            </gs-field>

            <div v-else class="gs-promo-dashboard__portal-static">
              <span class="gs-promo-dashboard__portal-label">{{ $t('frontend_portal') }}</span>
              <span class="gs-promo-dashboard__portal-value">{{ funnelResource.name }}</span>
            </div>
          </div>
        </header>

        <affiliate-portal-shell>
          <template slot="header">
            <div v-if="assetsTab !== ''" class="gs-promo-dashboard__section-bar">
              <gs-button variant="outline" size="sm" @click="goBackToPromoToolsNavigation()">
                <gs-icon name="arrow-left" size="16" />
                {{ $t('frontend_affiliate_promo_back_to_promo_tools') }}
              </gs-button>

              <h2 class="gs-promo-dashboard__section-title">{{ currentAssetTitle }}</h2>

              <div v-if="showCompletionToggle" class="gs-promo-dashboard__done">
                <span class="gs-promo-dashboard__done-label">
                  {{ $t('frontend_affiliate_promo_mark_as_completed') }}
                </span>
                <gd-toggle
                  v-model="funnelResource.affiliate_resource[currentAssetProperty]"
                  :value="funnelResource.affiliate_resource[currentAssetProperty]"
                  @change="toggleSwitchValue(currentAssetTypeId)"
                />
              </div>
            </div>
          </template>

          <template slot="hero">
            <link-hero
              v-if="affiliateLink"
              :link="affiliateLink"
              :links="affiliateLinks"
              :selected-id="selectedAffiliateLinkId"
              @select="selectAffiliateLink"
              @copy="onCopy"
              @error="onError"
              @open="openAffiliateLink"
            />
          </template>

          <template slot="rail">
            <section-rail
              v-if="sectionItems.length"
              :items="sectionItems"
              :current="assetsTab"
              @select="openPromotoolsSection"
            />
          </template>

          <!-- The section views raise their own `$gsToast` confirmations and
               `promotools-tabs` carries the single provider for this route;
               the layout mounts none of its own. -->
          <main-tab
            v-if="assetsTab === ''"
            :assets-tabs="assetsTabs"
            :funnel-resource="funnelResource"
            :show-elements="false"
            @openPromotoolsSection="openPromotoolsSection"
          />
          <promotools-tabs
            @select="assetsTab = $event"
            v-else
            :assets-tab="assetsTab"
            :funnel-resource="funnelResource"
            :direct-affiliate-links="directAffiliateLinks"
          />
        </affiliate-portal-shell>
      </div>

      <div v-else class="gs-promo-dashboard__empty">
        <p class="gs-promo-dashboard__empty-title">
          {{ $t('frontend_groovesell_resources_havent_signed_aff_funnel_desc') }}
        </p>
      </div>
    </v-wait>
  </div>
</template>

<script>
import axios from 'axios';
// Registers the two providers `VideoReviewsTab` parses review URLs with; the
// section view only imports the parser base.
import 'js-video-url-parser/lib/provider/youtube';
import 'js-video-url-parser/lib/provider/vimeo';
import Notify from '@groovepages/gd-ui-kit/notify';
import AffiliatePortalShell from '@/components/promotools/AffiliatePortalShell.vue';
import LinkHero from '@/components/promotools/LinkHero.vue';
import SectionRail from '@/components/promotools/SectionRail.vue';
import MainTab from '@/components/promotools/partials/tabs/MainTab.vue';
import PromotoolsTabs from '@/components/promotools/partials/PromotoolsTabs.vue';
import { portalSections, affiliateLinkId } from '@/components/promotools/portalSections';

export default {
  layout: 'defaultForAffiliates',
  middleware: 'auth',
  components: {
    AffiliatePortalShell,
    LinkHero,
    SectionRail,
    MainTab,
    PromotoolsTabs,
  },
  data() {
    return {
      funnel_id: this.$route.params.id,
      funnelResource: null,
      assetsTabs: [],
      assetsTab: '',
      selectedPromoLinkPermalink: '',
      selectedAffiliateLinkId: null,
    };
  },
  computed: {
    promoToolOptions() {
      if (!this.funnelResource || !this.funnelResource.remaining_resources) {
        return [];
      }

      return this.funnelResource.remaining_resources.map(promoTool => ({
        value: promoTool.permalink,
        text: promoTool.name,
      }));
    },
    portalTitle() {
      if (!this.funnelResource) {
        return this.$t('promo_dashboard');
      }

      return this.funnelResource.funnel_name || this.funnelResource.name;
    },
    currentAsset() {
      return this.assetsTabs.find(asset => asset.value === this.assetsTab) || null;
    },
    currentAssetTitle() {
      return this.currentAsset ? this.currentAsset.title : 'Promo Tools';
    },
    currentAssetProperty() {
      const asset = this.assetTabs.find(item => item.tab === this.assetsTab);
      return asset ? asset.property : '';
    },
    currentAssetTypeId() {
      const asset = this.assetsTabs.find(item => item.value === this.assetsTab);
      return asset ? asset.id : null;
    },
    // Both the hero and the Links section use the vendor-filtered `display_links`
    // list, so private landing pages stay limited to the affiliates they target.
    directAffiliateLinks() {
      if (!this.funnelResource || !this.funnelResource.display_links) {
        return [];
      }

      return this.funnelResource.display_links.filter(link => link.type === 'affiliate');
    },
    affiliateLinks() {
      return this.directAffiliateLinks;
    },
    selectedAffiliateLink() {
      if (this.selectedAffiliateLinkId === null) {
        return null;
      }

      return this.affiliateLinks.find(
        (link, index) => affiliateLinkId(link, index) === this.selectedAffiliateLinkId
      ) || null;
    },
    affiliateLink() {
      if (this.selectedAffiliateLink) {
        return this.selectedAffiliateLink.link;
      }

      if (this.funnelResource && this.funnelResource.home_page_link) {
        return this.funnelResource.home_page_link;
      }

      return this.affiliateLinks.length ? this.affiliateLinks[0].link : '';
    },
    sectionItems() {
      return portalSections(this.assetsTabs, this.funnelResource);
    },
    showCompletionToggle() {
      return this.assetsTab !== '' &&
        this.assetsTab !== 'links' &&
        this.assetsTab !== 'recruitmentLinks' &&
        this.assetsTab !== 'stats' &&
        this.currentAssetProperty &&
        this.funnelResource &&
        this.funnelResource.affiliate_resource;
    },
    hasMultipleResources() {
      return this.funnelResource &&
        this.funnelResource.remaining_resources &&
        this.funnelResource.remaining_resources.length > 1;
    },
    assetTabs() {
      return [
        { tab: 'swipes', property: 'swipes_completed' },
        { tab: 'blogReviews', property: 'blog_reviews_completed' },
        { tab: 'banners', property: 'banners_completed' },
        { tab: 'thankyouPageAds', property: 'thankyou_page_ads_completed' },
        { tab: 'logoutPageAds', property: 'logout_page_ads_completed' },
        { tab: 'facebookAds', property: 'facebook_ads_completed' },
        { tab: 'socialMediaPosts', property: 'social_media_posts_completed' },
        { tab: 'allArticles', property: 'press_releases_completed' },
        { tab: 'videoReviews', property: 'video_reviews_completed' },
        { tab: 'signatures', property: 'signatures_completed' },
      ];
    },
  },
  metaInfo() {
    return { title: this.$t('promo_dashboard') };
  },
  async created() {
    // `promo-dashboard` and not `dashboard`: the shared Stats section runs its
    // own `dashboard` waiter for the date-range reload and would otherwise tear
    // this page down while it fetches.
    this.$wait.start('promo-dashboard');

    // The route carries the funnel; the dashboard endpoint resolves it to the
    // promo resource this affiliate is registered against.
    const { data } = await axios.get(`affiliates/${this.funnel_id}/dashboard`);

    await this.fetchFunnelResource(data.resource_permalink);

    this.$wait.end('promo-dashboard');
  },
  methods: {
    goBackToPromoToolsNavigation() {
      this.assetsTab = '';
    },
    openPromotoolsSection(sectionName) {
      this.assetsTab = sectionName;
    },
    selectAffiliateLink(id) {
      this.selectedAffiliateLinkId = id;
    },
    openAffiliateLink() {
      if (!this.affiliateLink) {
        return;
      }

      const opened = window.open(this.affiliateLink, '_blank');

      if (opened) {
        opened.opener = null;
      }
    },
    async fetchFunnelResource(resourceId) {
      const funnelResourceData = await axios.get('fetch-funnel-resource', {
          params: { permalink: resourceId },
      });

      // A failed lookup leaves `funnelResource` null and the page falls back to
      // the "not signed up" panel below.
      if (funnelResourceData.data.status !== 'success') {
          return;
      }

      this.processFunnelResourceData(funnelResourceData.data.funnel_resource, resourceId);
    },
    processFunnelResourceData(resource, resourceId) {
      this.funnelResource = resource;
      this.selectedPromoLinkPermalink = resourceId;

      this.setupAssetsTabs();
    },
    setupAssetsTabs() {
      this.assetsTabs = [
        {
            title: 'Custom Landing Page Links',
            value: 'links',
            completed: 0,
            id: 0,
        },
        { title: 'Affiliate Recruitment Links', value: 'recruitmentLinks', completed: 0, id: -2 },
        {
            title: 'Stats',
            value: 'stats',
            completed: 0,
            id: 0,
        },
        {
            title: 'Swipes',
            value: 'swipes',
            completed: this.funnelResource.affiliate_resource.swipes_completed,
            id: 1,
        },
        {
            title: 'Blog Reviews',
            value: 'blogReviews',
            completed: this.funnelResource.affiliate_resource.blog_reviews_completed,
            id: 2,
        },
        {
            title: 'Articles',
            value: 'allArticles',
            completed: this.funnelResource.affiliate_resource.press_releases_completed,
            id: 8,
        },
        {
            title: 'Banners',
            value: 'banners',
            completed: this.funnelResource.affiliate_resource.banners_completed,
            id: 3,
        },
        {
            title: 'Thank You Page Ads',
            value: 'thankyouPageAds',
            completed: this.funnelResource.affiliate_resource.thankyou_page_ads_completed,
            id: 4,
        },
        {
            title: 'Logout Page Ads',
            value: 'logoutPageAds',
            completed: this.funnelResource.affiliate_resource.logout_page_ads_completed,
            id: 5,
        },
        {
            title: 'Social Media',
            value: 'socialMediaPosts',
            completed: this.funnelResource.affiliate_resource.social_media_posts_completed,
            id: 7,
        },
        {
            title: 'Video Reviews',
            value: 'videoReviews',
            completed: this.funnelResource.affiliate_resource.video_reviews_completed,
            id: 9,
        },
        {
            title: 'Facebook Ads',
            value: 'facebookAds',
            completed: this.funnelResource.affiliate_resource.facebook_ads_completed,
            id: 6,
        },
        {
            title: 'Signatures',
            value: 'signatures',
            completed: this.funnelResource.affiliate_resource.signatures_completed,
            id: 10,
        },
      ];
    },
    async changedPromoTool() {
      this.$loader.start('changedPromoTool');
      this.assetsTab = '';
      await this.fetchFunnelResource(this.selectedPromoLinkPermalink);
      this.$loader.end('changedPromoTool');
    },
    async toggleSwitchValue(type) {
      const propertiesMap = {
        1: 'swipes_completed',
        2: 'blog_reviews_completed',
        3: 'banners_completed',
        4: 'thankyou_page_ads_completed',
        5: 'logout_page_ads_completed',
        6: 'facebook_ads_completed',
        7: 'social_media_posts_completed',
        8: 'press_releases_completed',
        9: 'video_reviews_completed',
        10: 'signatures_completed'
      };

      const property = propertiesMap[type];
      if (!property) {
        return;
      }

      const foundIndex = this.assetsTabs.findIndex(asset => asset.value === this.getValueForType(type));
      if (foundIndex !== -1) {
        this.$set(
          this.assetsTabs[foundIndex],
          'completed',
          this.funnelResource.affiliate_resource[property]
        );
      }

      await axios.post('/toggle-affiliate-funnel-resource-switch', {
        type,
        id: this.funnelResource.affiliate_resource.id,
        value: this.funnelResource.affiliate_resource[property],
      });
    },
    getValueForType(type) {
        const valueMap = {
            1: 'swipes',
            2: 'blogReviews',
            3: 'banners',
            4: 'thankyouPageAds',
            5: 'logoutPageAds',
            6: 'facebookAds',
            7: 'socialMediaPosts',
            8: 'allArticles',
            9: 'videoReviews',
            10: 'signatures'
        };
        return valueMap[type] || '';
    },
    onCopy() {
      Notify.success(this.$t('copy_success'));
    },
    onError() {
      Notify.fail(this.$t('frontend_unable_copy_to_clipboard'));
    },
  },
  watch: {
    funnelResource() {
      this.selectedAffiliateLinkId = null;
    },
  },
};
</script>

<style scoped>
.gs-promo-dashboard {
  min-width: 0;
  color: var(--gs-foreground);
}

.gs-promo-dashboard__inner {
  max-width: 1280px;
  margin: 0 auto;
  min-width: 0;
}

.gs-promo-dashboard__page-header {
  display: flex;
  flex-wrap: wrap;
  gap: 16px 20px;
  align-items: flex-start;
  justify-content: space-between;
  min-width: 0;
  margin: 20px 20px 0;
  padding: 18px 20px;
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-lg);
  box-shadow: var(--gs-shadow-sm);
}

.gs-promo-dashboard__identity {
  flex: 1 1 320px;
  min-width: 0;
}

.gs-promo-dashboard__eyebrow {
  margin: 0 0 5px;
  color: var(--gs-primary);
  font-size: 10px;
  font-weight: 800;
  letter-spacing: .08em;
  text-transform: uppercase;
}

.gs-promo-dashboard__page-header h1 {
  margin: 0;
  overflow-wrap: anywhere;
  font-size: 24px;
  font-weight: 650;
  line-height: 1.25;
}

.gs-promo-dashboard__lede {
  max-width: 760px;
  margin: 6px 0 0;
  color: var(--gs-muted-foreground);
  font-size: 13px;
  line-height: 1.45;
}

.gs-promo-dashboard__portal {
  flex: 0 1 auto;
  min-width: 0;
}

.gs-promo-dashboard__portal-select {
  width: 360px;
  max-width: 100%;
}

.gs-promo-dashboard__portal-static {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.gs-promo-dashboard__portal-label {
  color: var(--gs-muted-foreground);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .06em;
  text-transform: uppercase;
}

.gs-promo-dashboard__portal-value {
  overflow-wrap: anywhere;
  font-size: 14px;
  font-weight: 600;
}

.gs-promo-dashboard__section-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 16px;
  align-items: center;
  min-width: 0;
}

.gs-promo-dashboard__section-title {
  flex: 1 1 auto;
  min-width: 0;
  margin: 0;
  overflow-wrap: anywhere;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.3;
}

.gs-promo-dashboard__done {
  display: flex;
  flex: none;
  gap: 10px;
  align-items: center;
}

.gs-promo-dashboard__done-label {
  color: var(--gs-muted-foreground);
  font-size: 13px;
  font-weight: 600;
}

.gs-promo-dashboard__empty {
  max-width: 640px;
  margin: 40px auto;
  padding: 24px;
  text-align: center;
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-lg);
  box-shadow: var(--gs-shadow-sm);
}

.gs-promo-dashboard__empty-title {
  margin: 0;
  font-size: 16px;
  font-weight: 650;
  line-height: 1.4;
}

.gs-promo-dashboard__portal { width: min(100%, 420px); }

@media (max-width: 640px) {
  .gs-promo-dashboard__page-header {
    margin: 12px 12px 0;
    padding: 14px 16px;
  }

  .gs-promo-dashboard__page-header h1 {
    font-size: 20px;
  }

  .gs-promo-dashboard__portal,
  .gs-promo-dashboard__portal-select {
    width: 100%;
  }

  .gs-promo-dashboard__section-title {
    flex: 1 1 100%;
    order: -1;
    font-size: 16px;
  }
}
</style>
